import { StyledScreenReaderSpan, namedUuid } from '@tvg/fuse-utils';
import PropTypes from 'prop-types';
import React from 'react';
import { componentConfig } from './config';
import { StyledRadio, StyledRadioInput, StyledRadioLabel } from './styled-radio';

const { forwardRef, useCallback, useEffect, useMemo, useRef } = React;

const propTypes = {
	analyticsId: PropTypes.string,
	className: PropTypes.string,
	clickCallback: PropTypes.func,
	dataTestId: PropTypes.string,
	disabled: PropTypes.bool,
	fullWidth: PropTypes.bool,
	marginBottom: PropTypes.bool,
	marginRight: PropTypes.bool,
	radioName: PropTypes.string,
	radioLabel: PropTypes.string,
	srLabel: PropTypes.string,
	style: PropTypes.object,
};

const defaultProps = {
	analyticsId: undefined,
	className: '',
	clickCallback: undefined,
	dataTestId: '',
	disabled: false,
	fullWidth: false,
	marginBottom: false,
	marginRight: false,
	radioName: '',
	radioLabel: '',
	srLabel: '',
	style: undefined,
};

const Radio = forwardRef(
	(
		{
			analyticsId,
			className,
			clickCallback,
			dataTestId,
			disabled,
			fullWidth,
			marginBottom,
			marginRight,
			radioLabel,
			radioName,
			srLabel,
			style,
			...others
		},
		ref,
	) => {
		// Component config
		const { label, srLabel: srLabelConfig } = componentConfig;
		// Unique IDs
		const inputId = useMemo(() => namedUuid('fuse-radio-input'), []);
		// Refs
		const inputRef = useRef();
		// Event handler
		const radioClickHandler = useCallback((e) => {
			// Run callback
			if (typeof clickCallback === 'function') clickCallback(e);
		}, []);

		useEffect(() => {
			const uA = navigator.userAgent;
			const isSafariDesktop =
				/Safari/i.test(uA) && !/Chrome/i.test(uA) && !/Mobi|Android/i.test(uA);

			// Fix for Safari desktop radio focus outline to show only on keyboard focus
			if (isSafariDesktop) inputRef.current.classList.add('fuse-radio__input--safari-focus');
		}, []);

		return (
			<StyledRadio
				className={className}
				data-analyticsid={analyticsId}
				data-testid={dataTestId || 'fuse-radio-test-id'}
				fullWidth={fullWidth}
				marginBottom={marginBottom}
				marginRight={marginRight}
				ref={ref}
				style={style}
			>
				<StyledRadioInput
					disabled={disabled}
					id={inputId}
					name={radioName}
					onClick={(e) => radioClickHandler(e)}
					ref={inputRef}
					type="radio"
					{...others}
				/>
				<StyledRadioLabel
					color={label.color}
					disabled={disabled}
					htmlFor={inputId}
					size={label.size}
					tag={label.tag}
					type={label.type}
				>
					{radioLabel}
					{srLabel && (
						<StyledScreenReaderSpan className={srLabelConfig.className}>
							{' '}
							{srLabel}
						</StyledScreenReaderSpan>
					)}
				</StyledRadioLabel>
			</StyledRadio>
		);
	},
);

Radio.propTypes = propTypes;
Radio.defaultProps = defaultProps;

export default Radio;
export { Radio };
